// Floating layers (menus) are portalled out of their trigger's container and positioned with `position: fixed`, so an
// overflow-clipping ancestor (the 64 px navigation rail, a scrolling column, a card) can never cut them. This module
// holds the pure placement math (flip to the side with room, then clamp into the viewport) and the ownership lookup a
// container uses to treat a portalled layer as "inside" itself.

export type FloatingPlacement = "down-end" | "down-start" | "up-start" | "right-start" | "right-end";

export interface AnchorRect {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

export interface FloatingPosition {
  top: number;
  left: number;
  /** Height available to the layer; it scrolls internally beyond this. */
  maxHeight: number;
  /** The side actually used after flipping. */
  side: "down" | "up" | "right" | "left";
}

/** Attribute a portalled layer carries: the id of the element that owns it (inside the original container). */
export const FLOATING_OWNER_ATTR = "data-floating-owner";

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), Math.max(min, max));

/**
 * Places a layer of `size` next to `anchor` inside a `viewport`, keeping `margin` px from every edge. The preferred
 * side flips when the layer does not fit there but fits (or has more room) on the opposite side.
 */
export function placeFloating(
  anchor: AnchorRect,
  size: { width: number; height: number },
  viewport: { width: number; height: number },
  placement: FloatingPlacement,
  gap = 4,
  margin = 8,
): FloatingPosition {
  const maxHeight = Math.max(0, viewport.height - margin * 2);
  const height = Math.min(size.height, maxHeight);
  const width = Math.min(size.width, Math.max(0, viewport.width - margin * 2));
  let top: number;
  let left: number;
  let side: FloatingPosition["side"];

  if (placement === "right-start" || placement === "right-end") {
    const roomRight = viewport.width - margin - (anchor.right + gap);
    const roomLeft = anchor.left - gap - margin;
    side = roomRight >= width || roomRight >= roomLeft ? "right" : "left";
    left = side === "right" ? anchor.right + gap : anchor.left - gap - width;
    top = placement === "right-start" ? anchor.top : anchor.bottom - height;
  } else {
    const roomBelow = viewport.height - margin - (anchor.bottom + gap);
    const roomAbove = anchor.top - gap - margin;
    const prefersUp = placement === "up-start";
    if (prefersUp) side = roomAbove >= height || roomAbove >= roomBelow ? "up" : "down";
    else side = roomBelow >= height || roomBelow >= roomAbove ? "down" : "up";
    top = side === "down" ? anchor.bottom + gap : anchor.top - gap - height;
    left = placement === "down-end" ? anchor.right - width : anchor.left;
  }

  return {
    top: clamp(top, margin, viewport.height - margin - height),
    left: clamp(left, margin, viewport.width - margin - width),
    maxHeight,
    side,
  };
}

/** True when `node` sits inside `root`, directly or through a floating layer whose owner is inside `root`. */
export function isWithinOrOwnedBy(root: Element | null, node: Node | null): boolean {
  if (!root || !node) return false;
  if (root.contains(node)) return true;
  const el = node instanceof Element ? node : node.parentElement;
  const layer = el?.closest(`[${FLOATING_OWNER_ATTR}]`);
  if (!layer) return false;
  const owner = document.getElementById(layer.getAttribute(FLOATING_OWNER_ATTR) ?? "");
  return owner !== null && owner !== layer && isWithinOrOwnedBy(root, owner);
}
